/**
 * OPC UA per-bank node -> lift binding wire helpers.
 *
 * The setup wizard authors node -> lift bindings on the Banks step — before
 * Lifts & Floors — so it can only reference lifts by POSITION/index, not by a
 * real (not-yet-existing) lift UUID. LiftCloud persists this binding as an
 * `OpcUaNodeLiftIndexBindings` FlatBuffer (packed via
 * `opcUaNodeLiftIndexBindingsBuilder.ts`) and resolves each index to the real
 * lift.ID against the bank's ordered Lifts at CDO-assembly time. The
 * BankNodeLiftMatrix UI works with a flat Record<nodeKey, liftIndexString>
 * for convenient per-node Select state — this helper filters that map down to
 * only the entries that are safe to pack, so a half-filled matrix never
 * produces a malformed binding.
 */

/**
 * Filter the matrix UI's Record<nodeKey, liftIndexString> down to entries
 * whose value is a non-negative integer index (e.g. drops a cleared
 * selection). Drops entries whose value is not a non-negative integer index,
 * so a half-filled matrix never emits a malformed entry into the FlatBuffer
 * builder.
 */
export default function nodeLiftMapToValidEntries(
  bindings: Record<string, string>,
): Record<string, string> {
  return Object.entries(bindings)
    .filter(([, liftIndex]) => {
      // Guard on the raw string first: Number("") and Number("  ") are 0, not
      // NaN, so a cleared/blank selection would otherwise masquerade as index 0.
      const parsed = Number(liftIndex);
      return liftIndex.trim() !== "" && Number.isInteger(parsed) && parsed >= 0;
    })
    .reduce<Record<string, string>>((acc, [node, liftIndex]) => {
      acc[node] = liftIndex;
      return acc;
    }, {});
}
